"use client";
import { useAtom } from "jotai";
import { ReactNode, useEffect, useMemo } from "react";
import styled, { CSSProperties, RuleSet, keyframes } from "styled-components";
import { useOutsideClick } from "../hooks/useOutsideClick";
import { ArrowIcon } from "../icons/ArrowIcon";
import { drawerAtom } from "../store/layoutStore";
import { shadow } from "../styles/globalsStyles";

export type Side = "left" | "right" | "top" | "bottom";
interface DrawerProps {
  useDrawer: boolean;
  children: ReactNode;
  side: Side;
  forceOpen?: boolean;
  drawerId: string;
  hideArrow?: boolean;
  onCloseThisDrawer?: () => void;
  height?: CSSProperties["height"];
  extraStyles?: RuleSet<object>;
}
const Drawer = ({
  children,
  useDrawer,
  side,
  forceOpen,
  drawerId,
  hideArrow,
  onCloseThisDrawer,
  height,
  extraStyles
}: DrawerProps) => {
  const [globalDrawers, setGlobalDrawers] = useAtom(drawerAtom);

  const thisDrawerShow = useMemo(
    () => globalDrawers[drawerId],
    [drawerId, globalDrawers]
  );

  useEffect(() => {
    if (forceOpen) {
      setGlobalDrawers((state) => ({ ...state, [drawerId]: true }));
    }
  }, [drawerId, forceOpen, setGlobalDrawers]);

  const ref = useOutsideClick(() => {
    setGlobalDrawers((state) => ({ ...state, [drawerId]: false }));
    onCloseThisDrawer?.();
  });

  if (!useDrawer) return children;

  return (
    <>
      {!thisDrawerShow && !hideArrow && (
        <ArrowIcon
          style={{ position: "absolute", [side]: 0, width: 30, height: 30 }}
          onClick={(e) => {
            e.stopPropagation();
            // setisDrawerOpened(true);
            setGlobalDrawers((state) => ({ ...state, [drawerId]: true }));
          }}
        />
      )}

      <StyledDrawer
        $display={thisDrawerShow}
        ref={thisDrawerShow ? ref : undefined}
        $side={side}
        $height={height}
        $extraStyles={extraStyles}
      >
        {children}
      </StyledDrawer>
    </>
  );
};

//TODO: implement side dictionary
const slideInFromSide = keyframes`

  from {    
    right:-300px;
  }
  to {    
    right:0;
  }
`;
const slideInFromTop = keyframes`
  from {    
    top:-300px;
  }
  to {    
    bottom:0;
  }
`;

const StyledDrawer = styled.div<{
  $side: Side;
  $display: boolean;
  $height?: CSSProperties["height"];
  $extraStyles?: RuleSet<object>;
}>`
  display: ${(p) => (p.$display ? "block" : "none")};
  position: absolute;
  z-index: 9;
  ${(p) => p.$side + ": 0;"}
  animation: ${({ $side }) =>
    $side === "right" ? slideInFromSide : slideInFromTop} 0.4s ease-in-out;

  /* background-color: #fff; */
  height: ${({ $height }) => $height ?? "-webkit-fill-available"};
  /* width: ; */
  overflow: auto;
  ${shadow}
  ${(p) => p.$extraStyles}
`;

export default Drawer;
